/**
 * Global uncaught-error hooks + React boundary helpers → diagnosticLogger (Tauri JSONL path).
 */

import { diagnosticLogger } from './diagnosticLogger';
import { sanitizeDiagnosticText, sanitizeStack, sanitizePayload } from './diagnosticSanitizer';
import { installEventPipeLiveness } from './eventPipeLiveness';

function isRenderLoopMessage(error: unknown): boolean {
  const msg = String((error as Error)?.message || error || '');
  return (
    msg.includes('185')
    || msg.includes('Maximum update depth')
    || msg.includes('Too many re-renders')
  );
}

export function logReactBoundaryError(
  error: unknown,
  info?: { componentStack?: string | null },
  opts?: { renderLoop?: boolean },
): void {
  try {
    const renderLoop = opts?.renderLoop ?? isRenderLoopMessage(error);
    const rawMsg = String((error as Error)?.message || error || 'React error');
    const category = renderLoop ? 'react-185' : 'react';
    const message = renderLoop
      ? `render-loop: ${sanitizeDiagnosticText(rawMsg)}`
      : sanitizeDiagnosticText(rawMsg);
    const payload = sanitizePayload({
      stack: sanitizeStack((error as Error)?.stack),
      component_stack: info?.componentStack
        ? sanitizeDiagnosticText(String(info.componentStack).slice(0, 2000))
        : undefined,
      render_loop: renderLoop || undefined,
    });
    diagnosticLogger.log({
      level: 'error',
      category,
      message,
      payload: Object.keys(payload).length ? payload : undefined,
    });
  } catch {
    /* never propagate from error handler */
  }
}

/** Fetch/image load failures that reject without .catch() — not app bugs. */
function isBenignNetworkRejection(reason: unknown): boolean {
  const msg = reason instanceof Error ? reason.message : String(reason ?? '');
  return (
    msg.includes('Load failed')
    || msg.includes('Failed to fetch')
    || msg.includes('NetworkError')
  );
}

/** Best-effort component/file hint from a stack trace (React reportError / onClick paths). */
function extractComponentHint(stack?: string): string | undefined {
  if (!stack) return undefined;
  for (const line of stack.split('\n').slice(0, 12)) {
    const trimmed = line.trim();
    const fnMatch = trimmed.match(/^at\s+([A-Z][A-Za-z0-9_$]+)/);
    if (fnMatch?.[1] && !['Object', 'Promise', 'Generator'].includes(fnMatch[1])) {
      return fnMatch[1];
    }
    const fileMatch = trimmed.match(/\/([A-Za-z][A-Za-z0-9_-]+)\.(jsx?|tsx?)/);
    if (fileMatch?.[1] && !['index', 'main', 'chunk'].includes(fileMatch[1])) {
      return fileMatch[1];
    }
  }
  return undefined;
}

function logUncaught(message: string, payload?: Record<string, unknown>): void {
  try {
    diagnosticLogger.log({
      level: 'error',
      category: 'uncaught',
      message: sanitizeDiagnosticText(message),
      payload: payload ? sanitizePayload(payload) : undefined,
    });
  } catch {
    /* swallow */
  }
}

function logReportError(error: unknown): void {
  try {
    const err = error instanceof Error ? error : new Error(String(error ?? 'reportError'));
    const stack = sanitizeStack(err.stack);
    const component = extractComponentHint(err.stack);
    const renderLoop = isRenderLoopMessage(err);
    const category = renderLoop ? 'react-185' : 'react';
    const message = renderLoop
      ? `render-loop: ${sanitizeDiagnosticText(err.message)}`
      : `handler: ${sanitizeDiagnosticText(err.message)}`;
    const payload = sanitizePayload({
      stack,
      component,
      source: 'reportError',
      render_loop: renderLoop || undefined,
    });
    diagnosticLogger.log({
      level: 'error',
      category,
      message,
      payload: Object.keys(payload).length ? payload : undefined,
    });
  } catch {
    /* swallow */
  }
}

/** Register once at app init. Fleet JSONL path is desktop/Tauri; live stream still works when session active. */
export function installUncaughtDiagnosticHooks(): void {
  if (typeof window === 'undefined') return;
  const w = window as Window & { __maestroUncaughtHooksInstalled?: boolean };
  if (w.__maestroUncaughtHooksInstalled) return;
  w.__maestroUncaughtHooksInstalled = true;
  installEventPipeLiveness();

  window.addEventListener('error', (event) => {
    try {
      const err = event.error;
      const msg = sanitizeDiagnosticText(
        err instanceof Error ? err.message : String(event.message || 'Uncaught error'),
      );
      const stack = sanitizeStack(err instanceof Error ? err.stack : undefined);
      logUncaught(msg, {
        source: event.filename ? sanitizeDiagnosticText(event.filename.split('?')[0]) : undefined,
        lineno: event.lineno || undefined,
        colno: event.colno || undefined,
        stack,
        component: extractComponentHint(err instanceof Error ? err.stack : undefined),
      });
    } catch {
      /* swallow */
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    try {
      const reason = event.reason;
      if (isBenignNetworkRejection(reason)) {
        event.preventDefault();
        return;
      }
      const msg = sanitizeDiagnosticText(
        reason instanceof Error ? reason.message : String(reason ?? 'Unhandled rejection'),
      );
      logUncaught(`unhandledrejection: ${msg}`, {
        stack: sanitizeStack(reason instanceof Error ? reason.stack : undefined),
        component: extractComponentHint(reason instanceof Error ? reason.stack : undefined),
      });
    } catch {
      /* swallow */
    }
  });

  // React 18+/19 routes many event-handler and render exceptions through
  // window.reportError — they never reach window 'error' or ErrorBoundary.
  const nativeReportError = typeof window.reportError === 'function' ? window.reportError.bind(window) : null;
  window.reportError = (error: unknown) => {
    try {
      logReportError(error);
    } catch {
      /* swallow */
    }
    if (nativeReportError) {
      nativeReportError(error);
    } else if (error instanceof Error) {
      console.error(error);
    } else {
      console.error(error);
    }
  };
}
